import React, { useContext, useEffect, useState } from 'react';
import useForm, { proxiedPropertiesOf } from '@hooks/useForm/useForm';
import { ApiContext } from '@api/api';
import {
  CurrencyLockTypeEnum,
  EntityTypeEnum,
  OpeningModeEnum,
  SupplierNotificationTypeEnum,
} from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { isNullOrUndefined } from '@utils/utils';
import { Calendar } from 'primereact/calendar';
import dayjs from 'dayjs';
import {
  apiPragueFromDayjsLocal,
  shortDateFormat,
} from '@utils/dateWithTz';
import * as yup from 'yup';
import ManageTimeslot, { timeslotValidationSchema } from '@components/obsolete/TimeslotWithDefaults/ManageTimeslot.tsx';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import Section from '@components/obsolete/Section/Section.tsx';
import HorizontalFieldValue from '@components/obsolete/HorizontalFieldValue.tsx';
import InputShipmentStatus from '@components/obsolete/Form/InputShipmentStatus/InputShipmentStatus.tsx';
import InputResponsiblePersonPlain
  from '@components/obsolete/Form/InputResponsiblePerson/InputResponsiblePersonPlain.tsx';
import { Note } from '@components/styles.tsx';
import { RC, RR } from '@components/obsolete/RC.tsx';
import LabelsControlled from '@components/obsolete/Labels/LabelsControlled.tsx';
import { useTranslation } from 'react-i18next';
import { Field } from '@components/framework/formfield/Field.tsx';
import InputFolders from '@components/framework/input/InputFolders/InputFolders.tsx';
import CarrierDistributionSelector, {
  DistributionType,
} from '@components/RingilComponents/CarrierDistributionSelector/CarrierDistributionSelector.tsx';
import { buildMassEditRequest, SupplierNotificationMassEditData } from '@app/pages/customer/supplier-notification/sidebars/multiEditSupplierNotificationMapping.ts';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';

interface ComponentProps {
  supplierNotificationIds: string[];
  supplierNotificationTypes?: SupplierNotificationTypeEnum[];
  containsGroupDemand: boolean;
  containsExpedition: boolean;
  containsWaiting: boolean;
  showDistribution: boolean;
}

export const useCreateMultiEditSupplierNotificationSidebar = (onComplete: () => void) => {
  const [visible, setVisible] = useState(false);
  const { tr } = useTranslationLgs();

  const MultiEditSupplierNotificationSidebar: React.FC<ComponentProps> = ({
                                                                            supplierNotificationIds,
                                                                            supplierNotificationTypes = [],
                                                                            containsGroupDemand = false,
                                                                            containsExpedition = false,
                                                                            containsWaiting = false,
                                                                            showDistribution = true,
                                                                          }) => {
    const { loggedUser } = useContext(AuthenticatedUserContext);
    const { updateSupplierNotificationMassAction } = useContext(ApiContext);
    const { i18n } = useTranslation();

    const [loading, setLoading] = useState(false);

    const form = useForm<SupplierNotificationMassEditData>(
      yup.object().shape({
        supplierDeliverySlots: timeslotValidationSchema(false),
        customerDeliverySlots: timeslotValidationSchema(false),
        loadingDates: yup.array().when('overwriteDatesWithOpeningTimes', {
          is: true,
          then: yup.array().test(
            'loading-date-required',
            tr('MultiEditSupplierNotificationSidebar.loadingDateRequired', 'Loading date is required'),
            (value) => value != null && value.length > 0 && value[0] != null,
          ),
          otherwise: yup.array().nullable(),
        }),
        unloadingDates: yup.array().when('overwriteDatesWithOpeningTimes', {
          is: true,
          then: yup.array().test(
            'unloading-date-required',
            tr('MultiEditSupplierNotificationSidebar.unloadingDateRequired', 'Unloading date is required'),
            (value) => value != null && value.length > 0 && value[0] != null,
          ),
          otherwise: yup.array().nullable(),
        }),
        distribution: yup.object().when('createExpedition', {
          is: true,
          then: yup.object().shape({
            distributionType: yup.string(),
            price: yup.object().when('distributionType', {
              is: (type: string) => !!type,
              then: yup.object().shape({
                price: yup.number()
                  .typeError(tr('MultiEditSupplierNotificationSidebar.priceShouldBeNumber', 'Price should be number'))
                  .transform((value) => (isNaN(value) ? undefined : value))
                  .nullable()
                  .required(tr('MultiEditSupplierNotificationSidebar.mandatoryPrice', 'Price is required')),
                currency: yup.string().required(tr('CreateExpedition.mandatoryCurrency', 'Currency is required')),
              }).required(tr('MultiEditSupplierNotificationSidebar.mandatoryPrice', 'Price is required')),
              otherwise: yup.object().nullable(),
            }),
            selectedItems: yup.array().when('distributionType', {
              is: (type: string) => type === 'FIXED' || type === 'SPOT' || type === 'ASSIGN',
              then: yup.array()
                .min(1, tr('MultiEditSupplierNotificationSidebar.mandatoryCarriers', 'Please select at least one carrier'))
                .required(tr('MultiEditSupplierNotificationSidebar.mandatoryCarriers', 'Please select at least one carrier')),
              otherwise: yup.array().nullable(),
            }),
          }),
          otherwise: yup.object().nullable(),
        }),
      }),
      {
        responsiblePersonId: null,
        supplierDeliverySlots: { dayWithInterval: [] },
        customerDeliverySlots: { dayWithInterval: [] },
        shipmentStateCode: null,
        overwriteLoadingTimeslots: false,
        overwriteShipmentStateCode: false,
        overwriteResponsiblePersonId: false,
        overwriteDatesWithOpeningTimes: false,
        loadingDates: [],
        unloadingDates: [],
        overwriteLabels: true,
        labels: [],
        overwriteFolder: false,
        createExpedition: false,
      },
      () => {
        setLoading(true);
        updateSupplierNotificationMassAction(
          buildMassEditRequest(supplierNotificationIds, form.values),
          () => {
            setVisible(false);
            setLoading(false);
            onComplete();
          },
          null,
          {
            onValidationFailed: () => {
              setLoading(false);
            },
          },
        );
      },
      false,
      false,
    );

    if (isNullOrUndefined(form)) return <></>;

    const names = proxiedPropertiesOf<SupplierNotificationMassEditData>();

    return (
      <RingilSidebar
        visible={visible}
        heading={tr(`MultiEditSupplierNotificationSidebar.massUpdateAction`, `Mass update action`)}
        onBack={() => setVisible(false)}
        onContinue={() => form.validateAndSend()}
        loading={loading}
      >
        <Section
          label={tr(`MultiEditSupplierNotificationSidebar.iWantChangeShipmentState`, `I want change shipment state`)}
          showToggle={true}
          isDisabled={supplierNotificationTypes.includes(SupplierNotificationTypeEnum.DXX)}
          onVisibilityChanged={v => form.setFieldValue(names.overwriteShipmentStateCode, v)}
        >
          <HorizontalFieldValue
            label={tr(`MultiEditSupplierNotificationSidebar.shipmentState`, `Shipment state`)}
            value={
              <InputShipmentStatus
                code={form.values.shipmentStateCode}
                onChange={v => {
                  form.setFieldValue(names.shipmentStateCode, v);
                }}
              />
            }
          />
        </Section>
        <Section
          label={tr(`MultiEditSupplierNotificationSidebar.iWatchChangeResponsiblePerson`, `I want change responsible person`)}
          showToggle={true}
          isDisabled={supplierNotificationTypes.includes(SupplierNotificationTypeEnum.DXX)}
          onVisibilityChanged={v => form.setFieldValue(names.overwriteResponsiblePersonId, v)}
        >
          <HorizontalFieldValue
            label={tr(`MultiEditSupplierNotificationSidebar.responsiblePerson`, `Responsible person`)}
            value={<InputResponsiblePersonPlain responsiblePersonId={form.values.responsiblePersonId}
                                                onChange={v => form.setFieldValue(names.responsiblePersonId, v)} />}
          />
        </Section>
        <Section
          label={tr(`MultiEditSupplierNotificationSidebar.iWatchChangeDates`, `I want change dates`)}
          showToggle={true}
          isDisabled={supplierNotificationTypes.includes(SupplierNotificationTypeEnum.DXX)}
          onVisibilityChanged={v => {
            form.setFieldValue(names.overwriteLoadingTimeslots, v);
            if (v) {
              form.setFieldValue(names.supplierDeliverySlots, {
                dayWithInterval: [
                  {
                    day: apiPragueFromDayjsLocal(dayjs()),
                    utcDay: dayjs().utc().toISOString(),
                    openingMode: OpeningModeEnum.CLOSED,
                    intervals: [],
                  },
                ],
              });
              form.setFieldValue(names.customerDeliverySlots, {
                dayWithInterval: [
                  {
                    day: apiPragueFromDayjsLocal(dayjs()),
                    utcDay: dayjs().utc().toISOString(),
                    openingMode: OpeningModeEnum.CLOSED,
                    intervals: [],
                  },
                ],
              });
            }
          }}
        >
          <HorizontalFieldValue
            label={tr(`MultiEditSupplierNotificationSidebar.nakladka`, `Nakladka`)}
            value={
              <>
                <ManageTimeslot
                  entityId={null}
                  entityType={null}
                  timeslot={form.values.supplierDeliverySlots}
                  onChange={v => {
                    form.setFieldValue(names.supplierDeliverySlots, v);
                  }}
                  validationErrors={form.validationErrors}
                  validationPrefix={'supplierDeliverySlots.'}
                />
              </>
            }
          />
          <HorizontalFieldValue
            label={tr(`MultiEditSupplierNotificationSidebar.vykladka`, `Vykladka`)}
            value={
              <>
                <ManageTimeslot
                  entityId={null}
                  entityType={null}
                  timeslot={form.values.customerDeliverySlots}
                  onChange={v => {
                    form.setFieldValue(names.customerDeliverySlots, v);
                  }}
                  validationErrors={form.validationErrors}
                  validationPrefix={'customerDeliverySlots.'}
                />
              </>
            }
          />
        </Section>
        <Section
          label={tr(`MultiEditSupplierNotificationSidebar.changeDate`, `Change date`)}
          showToggle={true}
          isDisabled={supplierNotificationTypes.includes(SupplierNotificationTypeEnum.DXX)}
          onVisibilityChanged={v => {
            form.setFieldValue(names.loadingDates, []);
            form.setFieldValue(names.unloadingDates, []);
            form.setFieldValue(names.overwriteDatesWithOpeningTimes, v);
          }}
        >
          <Note>
            {tr(`MultiEditSupplierNotificationSidebar.exceptionsNotTaken`, `Possible exceptions in the opening time of the locations are not taken into account`)}
          </Note>
          <HorizontalFieldValue
            label={''}
            value={
              <>
                <RR>
                  <RC large={12} medium={12} small={12}>
                    <HorizontalFieldValue
                      label={tr(`CreateSupplierNotification.supplierTimeslot`, `Supplier timeslot`)}
                      value={
                        <>
                          <Calendar
                            value={form.values.loadingDates}
                            selectionMode={'range'}
                            locale={i18n.language}
                            dateFormat={shortDateFormat}
                            onChange={v => form.setFieldValue(names.loadingDates, v.value)}
                          />
                          {form.find('loadingDates') && (
                            <small className="p-error">{form.find('loadingDates')}</small>
                          )}
                        </>
                      }
                    />
                  </RC>
                  <RC large={12} medium={12} small={12}>
                    <HorizontalFieldValue
                      label={tr(`CreateSupplierNotification.customerTimeslot`, `Customer timeslot`)}
                      value={
                        <>
                          <Calendar
                            value={form.values.unloadingDates}
                            selectionMode={'range'}
                            locale={i18n.language}
                            dateFormat={shortDateFormat}
                            onChange={v => form.setFieldValue(names.unloadingDates, v.value)}
                          />
                          {form.find('unloadingDates') && (
                            <small className="p-error">{form.find('unloadingDates')}</small>
                          )}
                        </>
                      }
                    />
                  </RC>
                </RR>
              </>
            }
          />
        </Section>
        <Section
          label={tr(`MultiEditSupplierNotificationSidebar.overrideLabels`, `Override labels`)}
          showToggle={true}
          onVisibilityChanged={v => {
            form.setFieldValue(names.labels, []);
            form.setFieldValue(names.overwriteLabels, v);
          }}
        >
          <LabelsControlled
            entityType={EntityTypeEnum.SUPPLIER_NOTIFICATION}
            value={form.values.labels}
            onChange={v => {
              form.setFieldValue(names.labels, v);
            }}
          />
        </Section>
        <Section
          label={tr(`MultiEditSupplierNotificationSidebar.overwriteFolders`, `Přepsat složky`)}
          showToggle={true}
          onVisibilityChanged={v => {
            form.setFieldValue(names.folders, []);
            form.setFieldValue(names.overwriteFolder, v);
          }}
        >
          <Field label={tr('MAExp.vyberteSlozky', 'Vyberte složky (prázdné = odebrat ze všech složek)')}>
            <InputFolders
              folders={form.values.folders ?? []}
              onChange={v =>
                form.useProduce(d => {
                  d.folders = v;
                })
              }
              entityType={EntityTypeEnum.SUPPLIER_NOTIFICATION}
            />
          </Field>
        </Section>
        {showDistribution && (
          <Section
            label={tr(`MultiEditSupplierNotificationSidebar.createExpeditionAndAssignCarrier`, `Create expedition and assign carrier`)}
            showToggle={true}
            isDisabled={supplierNotificationTypes.includes(SupplierNotificationTypeEnum.DXX) || containsExpedition || containsGroupDemand || containsWaiting}
            onVisibilityChanged={v => {
              form.setFieldValue(names.createExpedition, v);
              form.setFieldValue(names.distribution, {
                distributionType: DistributionType.ASSIGN,
                selectedItems: [],
                price: { currency: loggedUser?.defaultCurrencyIsoCode, price: 0 },
                notifyAssignedCarrier: false,
                currencyLockType: CurrencyLockTypeEnum.NONE,
              });
            }}
          >
            <CarrierDistributionSelector
              allowedDistributionTypes={[DistributionType.ASSIGN]}
              distribution={form.values.distribution}
              onChange={v => {
                form.setFieldValue(form.names.distribution, v);
              }}
              validationErrors={form.validationErrors
                .filter(e => e.path?.startsWith('distribution'))
                .map(e => ({ path: e.path || '', message: e.message }))}
            />
          </Section>
        )}
      </RingilSidebar>
    );
  };

  return { MultiEditSupplierNotificationSidebar, setVisible };
};
